/**
 * CopyFromSource — copy a section or table word for word from any source
 * document into the current section. No model: the server reads the file
 * (exactly, for Word) or the library's extraction (other files) and proposes
 * the text for review.
 *
 *   step 1  which document      (Word files marked "exact")
 *   step 2  which section/table (the document's own outline)
 *   step 3  where it goes       (in place of the section's text, or after the
 *                                paragraph at the cursor)
 *
 * The result opens in the review window; Word changes only on Approve.
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  type CopyFromSourceBody,
  type CopyFromSourceResponse,
  copySectionFromSource,
  getSourceOutline,
  type SourceOutline,
} from "../../api/client";
import { getCursorParagraph } from "../../services/textSelection";
import { ChoiceList, FooterBar, Button, Notice, SearchField, Segmented, StepHeader } from "../../ds/components";

export interface CopySourceDoc {
  id: string;
  name: string;
}

export type CopyPlacement = "replace_section" | "after_text" | "replace_text";

export interface CopyFromSourceProps {
  runId: string;
  /** The run's module id of the section the copy goes into. */
  moduleId: string;
  /** Its number as shown ("2.6"). */
  sectionNumber: string;
  sectionTitle: string;
  documents: readonly CopySourceDoc[];
  initialPlacement?: CopyPlacement;
  /** A proposal was made — the host opens the review window. */
  onDone: (resp: CopyFromSourceResponse) => void;
  onCancel: () => void;
  /** The text the writer highlighted in Word. When set, the copy replaces
   *  exactly that text (any section of any source; no proposal is made — the
   *  host reviews ``resp.preview`` and applies it to the highlight). */
  highlightText?: string | null;
}

type Pick =
  | { kind: "section"; number: string; title: string }
  | { kind: "table"; index: number | null; caption: string };

export const isWordFile = (name: string): boolean => /\.docx$/i.test(name);

/** Word files first (exact copy), then the rest; within each, names with the
 *  newest year first, then by name. */
export function orderDocuments(docs: readonly CopySourceDoc[]): CopySourceDoc[] {
  const year = (n: string) => Math.max(0, ...(n.match(/(?:19|20)\d\d/g) ?? []).map(Number));
  return docs
    .slice()
    .sort(
      (a, b) =>
        Number(isWordFile(b.name)) - Number(isWordFile(a.name)) ||
        year(b.name) - year(a.name) ||
        a.name.localeCompare(b.name),
    );
}

/** The outline entry with the draft section's number and title, if any —
 *  preselected when the copy replaces the section. */
export function matchingSection(
  outline: SourceOutline,
  number: string,
  title: string,
): { number: string; title: string } | null {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const byBoth = outline.sections.find((s) => s.number === number && norm(s.title) === norm(title));
  const byTitle = outline.sections.filter((s) => norm(s.title) === norm(title));
  return (
    byBoth ?? (byTitle.length === 1 ? byTitle[0] : null) ?? outline.sections.find((s) => s.number === number) ?? null
  );
}

export function copyBody(
  doc: CopySourceDoc,
  pick: Pick,
  placement: CopyPlacement,
  anchor?: string,
): CopyFromSourceBody {
  const base = { doc_id: doc.id, doc_name: doc.name, placement, anchor_text: anchor };
  if (pick.kind === "table") {
    return pick.index !== null
      ? { ...base, kind: "table", table_index: pick.index }
      : { ...base, kind: "table", table_caption: pick.caption };
  }
  return { ...base, kind: "section", source_number: pick.number || undefined, source_title: pick.title };
}

export const CopyFromSource: React.FC<CopyFromSourceProps> = ({
  runId,
  moduleId,
  sectionNumber,
  sectionTitle,
  documents,
  initialPlacement = "replace_section",
  onDone,
  onCancel,
  highlightText = null,
}) => {
  const docs = useMemo(() => orderDocuments(documents), [documents]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [doc, setDoc] = useState<CopySourceDoc | null>(null);
  const [outline, setOutline] = useState<SourceOutline | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"section" | "table">("section");
  const [filter, setFilter] = useState("");
  const [pick, setPick] = useState<Pick | null>(null);
  const [placement, setPlacement] = useState<CopyPlacement>(highlightText ? "replace_text" : initialPlacement);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc || step !== 2) return;
    let live = true;
    setLoading(true);
    setError(null);
    setOutline(null);
    getSourceOutline(runId, doc.id)
      .then((o) => {
        if (!live) return;
        setOutline(o);
        const same = initialPlacement === "replace_section" ? matchingSection(o, sectionNumber, sectionTitle) : null;
        setPick(same ? { kind: "section", number: same.number, title: same.title } : null);
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [doc, step, runId, initialPlacement, sectionNumber, sectionTitle]);

  const q = filter.trim().toLowerCase();
  const shownDocs = q ? docs.filter((d) => d.name.toLowerCase().includes(q)) : docs;
  const shownSections = (outline?.sections ?? []).filter(
    (s) => !q || `${s.number} ${s.title}`.toLowerCase().includes(q),
  );
  const shownTables = (outline?.tables ?? []).filter((t) => !q || t.caption.toLowerCase().includes(q));

  const submit = async () => {
    if (!doc || !pick) return;
    setBusy(true);
    setError(null);
    try {
      let anchor: string | undefined;
      if (placement === "replace_text") {
        anchor = highlightText ?? undefined;
      } else if (placement === "after_text") {
        const cursor = await getCursorParagraph();
        if (!cursor?.text) {
          setError("Put your cursor in a paragraph of this section in Word, then try again.");
          return;
        }
        if (cursor.sectionNumber && cursor.sectionNumber !== sectionNumber) {
          setError(`Your cursor is in §${cursor.sectionNumber}. Put it in §${sectionNumber}, then try again.`);
          return;
        }
        anchor = cursor.text;
      }
      const resp = await copySectionFromSource(runId, moduleId, copyBody(doc, pick, placement, anchor));
      if (placement === "replace_text" || (placement === "replace_section" && resp.preview)) {
        if (!resp.preview) {
          setError("Nothing came back from that source. Pick another section or document.");
          return;
        }
        onDone(resp);
        return;
      }
      if (resp.redraft) {
        onDone(resp);
        return;
      }
      if (!resp.proposal) {
        setError(
          `§${sectionNumber} already has exactly this text, so there is nothing to change. Pick another section, or check that your cursor is in the section you mean.`,
        );
        return;
      }
      onDone(resp);
    } catch (err) {
      const e = err as { detail?: { message?: string } | string };
      const detail = typeof e?.detail === "object" ? e.detail?.message : undefined;
      setError(detail ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  const pickLabel = pick?.kind === "table" ? pick.caption : pick ? `${pick.number} ${pick.title}`.trim() : "";

  // Radio rows need string ids; map each outline entry to one.
  const sectionId = (n: string, t: string) => `s|${n}|${t}`;
  const tableId = (i: number | null, c: string, k: number) => `t|${i ?? `x${k}`}|${c}`;
  const pickId =
    pick?.kind === "section"
      ? sectionId(pick.number, pick.title)
      : pick?.kind === "table"
        ? tableId(
            pick.index,
            pick.caption,
            shownTables.findIndex((t) => t.caption === pick.caption && t.index === pick.index),
          )
        : null;

  const into = highlightText
    ? "into the text you highlighted"
    : sectionNumber
      ? `into §${sectionNumber}${sectionTitle ? ` ${sectionTitle}` : ""}`
      : "";
  const back = () => {
    setFilter("");
    setError(null);
    setStep((s) => (s === 3 ? 2 : 1));
  };
  const next = () => {
    setFilter("");
    setError(null);
    setStep((s) => (s === 1 ? 2 : 3));
  };

  const placements = highlightText
    ? ([
        [
          "replace_text",
          "Replace the text you highlighted",
          `“${highlightText.length > 120 ? `${highlightText.slice(0, 120)}…` : highlightText}”`,
        ],
      ] as const)
    : ([
        [
          "replace_section",
          sectionNumber ? `Replace the text of §${sectionNumber}` : "Replace this section's text",
          "Everything under the heading is replaced.",
        ],
        [
          "after_text",
          "After the paragraph at my cursor",
          sectionNumber ? `Your cursor must be in §${sectionNumber}.` : "Put your cursor in the section.",
        ],
      ] as const);

  // Full-pane step, like New document: header, scrolling body, one footer row.
  return (
    <div
      className="as-takeover"
      role="dialog"
      aria-modal="true"
      aria-label="Copy from a source"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <StepHeader
        onBack={step > 1 && !busy ? back : undefined}
        step={`Copy from a source · step ${step} of 3`}
        title={step === 1 ? "Which document?" : step === 2 ? "What should be copied?" : "Where should it go?"}
      >
        <p className="as-p">
          {step === 1
            ? `The text is copied exactly as written, without rewriting. Word files are copied exactly; other files give the text as it was extracted.${into ? ` It goes ${into}.` : ""}`
            : step === 2
              ? doc?.name
              : `${pickLabel} from ${doc?.name ?? ""}`}
        </p>
        {step === 1 && docs.length > 6 ? (
          <SearchField value={filter} onChange={setFilter} placeholder="Find a document" />
        ) : null}
        {step === 2 && outline ? (
          <>
            <Segmented<"section" | "table">
              label="Copy a"
              value={tab}
              onChange={setTab}
              items={[
                { id: "section", label: `Sections (${outline.sections.length})` },
                { id: "table", label: `Tables (${outline.tables.length})` },
              ]}
            />
            <SearchField
              value={filter}
              onChange={setFilter}
              placeholder={tab === "section" ? "Find a section" : "Find a table"}
            />
          </>
        ) : null}
      </StepHeader>

      <div className="as-scroll as-sunken as-takeover-body as-stack">
        {step === 1 ? (
          shownDocs.length === 0 ? (
            <p className="as-note">No documents in the source library.</p>
          ) : (
            <ChoiceList
              name="copy-doc"
              label="Source document"
              value={doc?.id ?? null}
              onChange={(id) => setDoc(shownDocs.find((d) => d.id === id) ?? null)}
              options={shownDocs.map((d) => ({
                id: d.id,
                label: d.name,
                meta: isWordFile(d.name) ? "Word file · copied exactly" : "Extracted text",
              }))}
            />
          )
        ) : null}

        {step === 2 ? (
          loading ? (
            <p className="as-note" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="as-spin" aria-hidden="true" /> Reading {doc?.name}
            </p>
          ) : outline ? (
            <>
              {outline.format === "extracted" ? (
                <Notice tone="info">
                  Not a Word file: the copy is the text extracted from it. Check table layout before you approve.
                </Notice>
              ) : null}
              {tab === "section" ? (
                <ChoiceList
                  name="copy-section"
                  label="Section to copy"
                  value={pickId}
                  onChange={(id) => {
                    const hit = shownSections.find((s) => sectionId(s.number, s.title) === id);
                    if (hit) setPick({ kind: "section", number: hit.number, title: hit.title });
                  }}
                  options={shownSections.map((s) => ({
                    id: sectionId(s.number, s.title),
                    label: (
                      <span style={{ paddingLeft: Math.max(0, s.level - 1) * 12 }}>
                        {s.number ? <span className="as-mono">{s.number} </span> : null}
                        {s.title}
                      </span>
                    ),
                  }))}
                />
              ) : (
                <ChoiceList
                  name="copy-table"
                  label="Table to copy"
                  value={pickId}
                  onChange={(id) => {
                    const k = shownTables.findIndex((t, i) => tableId(t.index, t.caption, i) === id);
                    const hit = shownTables[k];
                    if (hit) setPick({ kind: "table", index: hit.index, caption: hit.caption });
                  }}
                  options={shownTables.map((t, i) => ({
                    id: tableId(t.index, t.caption, i),
                    label: t.caption,
                    meta: t.section ? `In section ${t.section}` : undefined,
                  }))}
                />
              )}
            </>
          ) : null
        ) : null}

        {step === 3 ? (
          <>
            <ChoiceList
              name="copy-placement"
              label="Where it goes"
              value={placement}
              onChange={(id) => setPlacement(id as CopyPlacement)}
              options={placements.map(([key, label, meta]) => ({ id: key, label, meta }))}
            />
            <p className="as-note">You review the change before anything in Word changes.</p>
          </>
        ) : null}

        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>

      <FooterBar>
        <Button onClick={onCancel}>Cancel</Button>
        {step < 3 ? (
          <Button variant="primary" disabled={step === 1 ? !doc : !pick} onClick={next}>
            Next
          </Button>
        ) : (
          <Button variant="primary" disabled={busy || !pick} onClick={() => void submit()}>
            {busy ? "Copying" : "Copy for review"}
          </Button>
        )}
      </FooterBar>
    </div>
  );
};

export default CopyFromSource;
